"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

import {
  TubesHeroBackground,
  type TubeHandoff,
} from "@/components/photon/tubes-hero-background";
import { cn } from "@/lib/utils";

type DockTransition = {
  sequence: number;
  fromId: string;
  toId: string;
  startedAt: number;
  directionX: number;
  directionY: number;
};

type PhotonLightDockContextValue = {
  activeDockId: string | null;
  transition: DockTransition | null;
  registerDock: (dockId: string, element: HTMLDivElement | null) => void;
};

const PhotonLightDockContext =
  createContext<PhotonLightDockContextValue | null>(null);
const HANDOFF_DURATION_MS = 780;

function normalizeDirection(x: number, y: number) {
  const magnitude = Math.hypot(x, y);

  if (magnitude < 0.0001) {
    return { x: 1, y: 0 };
  }

  return {
    x: x / magnitude,
    y: y / magnitude,
  };
}

function getDockCenter(element: HTMLDivElement) {
  const rect = element.getBoundingClientRect();

  return {
    x: rect.left + rect.width * 0.5,
    y: rect.top + rect.height * 0.5,
    width: rect.width,
    height: rect.height,
  };
}

export function PhotonLightDockProvider({
  children,
}: {
  children: ReactNode;
}) {
  const docksRef = useRef(new Map<string, HTMLDivElement>());
  const frameRef = useRef<number | null>(null);
  const activeDockIdRef = useRef<string | null>(null);
  const transitionSequenceRef = useRef(0);
  const [activeDockId, setActiveDockId] = useState<string | null>(null);
  const [transition, setTransition] = useState<DockTransition | null>(null);

  const measureActiveDock = useCallback(() => {
    frameRef.current = null;

    const viewportMidpoint = window.innerHeight * 0.5;
    let nextDockId: string | null = null;
    let closestDistance = Number.POSITIVE_INFINITY;

    docksRef.current.forEach((element, dockId) => {
      const rect = element.getBoundingClientRect();

      if (rect.width < 1 || rect.height < 1) {
        return;
      }

      const centerY = rect.top + rect.height * 0.5;
      const distance = Math.abs(centerY - viewportMidpoint);

      if (distance >= closestDistance) {
        return;
      }

      closestDistance = distance;
      nextDockId = dockId;
    });

    if (!nextDockId || nextDockId === activeDockIdRef.current) {
      return;
    }

    const previousDockId = activeDockIdRef.current;
    const nextElement = docksRef.current.get(nextDockId);
    const previousElement = previousDockId
      ? docksRef.current.get(previousDockId)
      : null;

    activeDockIdRef.current = nextDockId;
    setActiveDockId(nextDockId);

    if (!previousDockId || !previousElement || !nextElement) {
      setTransition(null);
      return;
    }

    const previousCenter = getDockCenter(previousElement);
    const nextCenter = getDockCenter(nextElement);
    const direction = normalizeDirection(
      nextCenter.x - previousCenter.x,
      nextCenter.y - previousCenter.y
    );

    transitionSequenceRef.current += 1;
    setTransition({
      sequence: transitionSequenceRef.current,
      fromId: previousDockId,
      toId: nextDockId,
      startedAt: performance.now(),
      directionX: direction.x,
      directionY: direction.y,
    });
  }, []);

  const requestMeasurement = useCallback(() => {
    if (frameRef.current !== null) {
      return;
    }

    frameRef.current = window.requestAnimationFrame(measureActiveDock);
  }, [measureActiveDock]);

  const registerDock = useCallback(
    (dockId: string, element: HTMLDivElement | null) => {
      if (element) {
        docksRef.current.set(dockId, element);
      } else {
        docksRef.current.delete(dockId);
      }

      requestMeasurement();
    },
    [requestMeasurement]
  );

  useEffect(() => {
    requestMeasurement();

    window.addEventListener("scroll", requestMeasurement, { passive: true });
    window.addEventListener("resize", requestMeasurement);

    return () => {
      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current);
      }

      window.removeEventListener("scroll", requestMeasurement);
      window.removeEventListener("resize", requestMeasurement);
    };
  }, [requestMeasurement]);

  useEffect(() => {
    if (!transition) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setTransition((currentTransition) =>
        currentTransition?.sequence === transition.sequence
          ? null
          : currentTransition
      );
    }, HANDOFF_DURATION_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [transition]);

  return (
    <PhotonLightDockContext.Provider
      value={{ activeDockId, transition, registerDock }}
    >
      {children}
    </PhotonLightDockContext.Provider>
  );
}

function usePhotonLightDockContext() {
  const context = useContext(PhotonLightDockContext);

  if (!context) {
    throw new Error(
      "PhotonLightDock must be used inside PhotonLightDockProvider"
    );
  }

  return context;
}

export function PhotonLightDock({
  dockId,
  activeOriginX,
  className,
}: {
  dockId: string;
  activeOriginX?: number;
  className?: string;
}) {
  const dockRef = useRef<HTMLDivElement>(null);
  const { activeDockId, transition, registerDock } =
    usePhotonLightDockContext();
  const isActive = activeDockId === dockId;
  const [hasEverActivated, setHasEverActivated] = useState(false);
  const [inactiveDirection, setInactiveDirection] = useState<{
    x: number;
    y: number;
  } | null>(null);

  useEffect(() => {
    registerDock(dockId, dockRef.current);

    return () => {
      registerDock(dockId, null);
    };
  }, [dockId, registerDock]);

  useEffect(() => {
    if (isActive || transition?.toId === dockId) {
      setHasEverActivated(true);
    }
  }, [dockId, isActive, transition]);

  useEffect(() => {
    if (transition?.fromId !== dockId) {
      return;
    }

    setInactiveDirection({
      x: transition.directionX,
      y: transition.directionY,
    });
  }, [dockId, transition]);

  let handoff: TubeHandoff | null = null;

  if (transition?.fromId === dockId) {
    handoff = {
      sequence: transition.sequence,
      phase: "outgoing",
      directionX: transition.directionX,
      directionY: transition.directionY,
      startedAt: transition.startedAt,
      durationMs: HANDOFF_DURATION_MS,
    };
  } else if (transition?.toId === dockId) {
    handoff = {
      sequence: transition.sequence,
      phase: "incoming",
      directionX: -transition.directionX,
      directionY: -transition.directionY,
      startedAt: transition.startedAt,
      durationMs: HANDOFF_DURATION_MS,
    };
  }

  const effectOpacity = handoff
    ? 1
    : isActive
      ? 0.96
      : hasEverActivated
        ? 0.22
        : 0;

  return (
    <div
      ref={dockRef}
      className={cn(
        "relative isolate overflow-hidden bg-transparent",
        className
      )}
    >
      <TubesHeroBackground
        active={isActive}
        activeOriginX={activeOriginX}
        className="absolute inset-0 z-0"
        effectOpacity={effectOpacity}
        enablePointerTracking={false}
        handoff={handoff}
        inactiveDirection={!isActive ? inactiveDirection : null}
        renderBaseBackground={false}
      />
      <div
        className={cn(
          "absolute inset-0 bg-[radial-gradient(circle_at_center,hsl(var(--foreground)/0.05),transparent_65%)] transition-opacity duration-500",
          handoff || isActive ? "opacity-100" : "opacity-75"
        )}
      />
    </div>
  );
}
